import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Filter, { FilterModal, FilterSection } from './Filter';
import CheckboxFilterByType from './FilterComponents/CheckboxFilterByType';
import { Defaults } from '../../../api/searchApiConstants';
import { PICKLEBALL_UTR_CEILING } from '../../../utils/normalizePlayerSearchFilters';

import styles from '../search.scss';

const SportTypeFilter = (props) => {
    const {
        clearFilters,
        filters,
        filterInputs,
        handleFilterClick,
        filterModal,
        closeModals,
        searchTypes,
        handleFilterSubmit,
        handleFilterUpdate,
        mobileOnly,
        desktopOnly,
        'data-testid': dataTestId,
    } = props;
    const isInitialMount = useRef(true);
    useEffect(() => {
        // Skip the first run so URL-hydrated utrMin/utrMax (NL search, bookmarked links)
        // are preserved rather than clobbered by the per-sport defaults below. The leaked
        // pickleball-doubles config on a tennis-inclusive search (HUB-1178) is normalized
        // at the hydration source in SearchResults via normalizePlayerRatingFilters, so it
        // no longer needs handling here.
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        const isPickleballOnly = filterInputs?.sportTypes?.pickleball && !filterInputs?.sportTypes?.tennis;
        if (isPickleballOnly) {
            // Pickleball UTR-P ratings cap at 10, so a pickleball-only search clamps to that range.
            handleFilterUpdate({
                utrTeamType: 'doubles',
                utrMin: Defaults.DEFAULT_UTR_MIN,
                utrMax: PICKLEBALL_UTR_CEILING,
            });
        } else {
            // DEFAULT_UTR_MAX (16) is the app-wide "no upper bound" sentinel: buildSearchParams
            // omits it, leaving a tennis search unbounded.
            handleFilterUpdate({
                utrTeamType: 'singles',
                utrMin: Defaults.DEFAULT_UTR_MIN,
                utrMax: Defaults.DEFAULT_UTR_MAX,
            });
        }
    }, [filterInputs?.sportTypes?.pickleball, filterInputs?.sportTypes?.tennis, filterInputs?.type]);

    const handleCheckboxChange = (value) => {
        const currentFilters = filterInputs.sportTypes;
        const isDeselecting = currentFilters[value];
        const selectedCount = Object.values(currentFilters).filter(val => val).length;

        if (isDeselecting && selectedCount === 1) {
            return;
        }

        handleFilterUpdate({
            sportTypes: {
                ...currentFilters,
                [value]: !currentFilters[value],
            },
        });
    };

    const sportTypeLabelTennis = 'Tennis';
    const sportTypeLabelPickleball = 'Pickleball';
    const sportLabel = 'Sport';
    let sportTypeLabel;

    if (filters?.sportTypes?.tennis && filters?.sportTypes?.pickleball) {
        sportTypeLabel = sportLabel;
    } else if (filters?.sportTypes?.tennis) {
        sportTypeLabel = sportTypeLabelTennis;
    } else {
        sportTypeLabel = sportTypeLabelPickleball;
    }

    return (
        <div className={styles.filterWrapper}>
            <Filter
                filterName={'sportType'}
                handleFilterClick={handleFilterClick}
                isActive={(filters?.sportTypes && Object.values(filters.sportTypes).indexOf(true) > -1) || filterModal === 'sportType'}
                mobileOnly={mobileOnly}
                desktopOnly={desktopOnly}
                data-testid={dataTestId}
            >
                {sportTypeLabel}
            </Filter>
            {filterModal === 'sportType' && (
                <FilterModal
                    title={'Sport Type'}
                    handleFilterSubmit={handleFilterSubmit}
                    onCloseClick={closeModals}
                    clearFilters={() => clearFilters('sportType')}
                    data-testid={dataTestId}
                >
                    <FilterSection>
                        <CheckboxFilterByType
                            searchTypes={searchTypes}
                            searchTypesPropName={`sportTypes`}
                            filterInputs={filterInputs}
                            filtersPropName={`sportTypes`}
                            onChange={handleCheckboxChange}
                            data-testid={dataTestId && `${dataTestId}.options`}
                        />
                    </FilterSection>
                </FilterModal>
            )}
        </div>
    );
};
SportTypeFilter.propTypes = {
    filterInputs: PropTypes.instanceOf(Object),
    filters: PropTypes.instanceOf(Object),
    'data-testid': PropTypes.string,
};
SportTypeFilter.defaultProps = {
    filterInputs: {},
    filters: {},
    'data-testid': null,
};

export default SportTypeFilter;
